import {
	Contact as ServerContact,
	ContactAdditionalInformation,
	EmailInformation,
	AddressInformation,
	useGetMeLazyQuery,
	useGetMyPaymentConditionsLazyQuery,
	EditablePaymentCondition as ServerPaymentConditions,
	PhoneInformation,
	DateInformation,
	PhoneType,
} from "@repo/server-api";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
	ResultFromData,
	ResultFromException,
	useNotificationGraphql,
} from "../../../shared/src/common/NotificationGraphlqlHook";

import type { GraphqlQueryResult } from "../../../shared/src/common/NotificationGraphlqlHook";
import type { Account, TermsOfPayment } from "../types/ContactTypes";

const infoGetter = <TInfo extends ContactAdditionalInformation & { __typename?: string }>(
	contact: ServerContact,
	typeInfo: string,
): TInfo[] | null => {
	const infos = contact.additionalInformation.filter((info): info is TInfo => {
		return (info as unknown as TInfo)?.__typename === typeInfo;
	});

	return infos.length > 0 ? infos : null;
};

const mapPaymentConditions = (
	nodes: ServerPaymentConditions[],
	t: (key: string) => string,
): Promise<TermsOfPayment[]> => {
	const entries: TermsOfPayment[] =
		nodes.map((node) => {
			const selectedEntryServer =
				node.entries.find((entry) => entry.id === node.selectedEntry) ?? null;
			const selectedEntry =
				selectedEntryServer != null
					? {
							id: selectedEntryServer.id,
							name:
								selectedEntryServer.discount +
								"% " +
								t("in") +
								" " +
								selectedEntryServer.dueDate +
								" " +
								t("days"),
						}
					: null;
			return {
				id: node.paymentInformationId,
				company: {
					id: node.companyId,
					name: node.displayName,
				},
				entries:
					node.entries.map((entry) => ({
						id: entry.id,
						name: entry.discount + "% " + t("in") + " " + entry.dueDate + " " + t("days"),
					})) ?? [],
				selectedEntry: selectedEntry,
			};
		}) ?? [];

	return Promise.resolve(entries);
};

export async function mapAccount(
	node: ServerContact,
	paymentConditions: TermsOfPayment[],
): Promise<Account> {
	const emailInfo = infoGetter<ContactAdditionalInformation>(node, "EmailInformation")?.[0] || null;
	const accountAddressInfo =
		infoGetter<ContactAdditionalInformation>(node, "AddressInformation")?.[0] || null;
	const phoneInfo =
		infoGetter<ContactAdditionalInformation>(node, "PhoneInformation")?.filter<PhoneInformation>(
			(info): info is PhoneInformation => (info as PhoneInformation).phoneType === PhoneType.Work,
		)[0] || null;
	const mobileInfo =
		infoGetter<ContactAdditionalInformation>(node, "PhoneInformation")?.filter<PhoneInformation>(
			(info): info is PhoneInformation => (info as PhoneInformation).phoneType === PhoneType.Mobile,
		)[0] || null;
	const birthdayInfo =
		infoGetter<ContactAdditionalInformation>(node, "DateInformation")?.[0] || null;

	return {
		id: node.id,
		name: node.displayName,
		firstName: node.firstName,
		lastName: node.lastName,
		salutation: node.salutation,
		title: node.title,
		accountType: node.displayContactType,
		phone: phoneInfo ? (phoneInfo as PhoneInformation).number : "",
		mobile: mobileInfo ? (mobileInfo as PhoneInformation).number : "",
		email: emailInfo ? (emailInfo as EmailInformation).emailAddress : "",
		birthday:
			birthdayInfo && (birthdayInfo as DateInformation).date
				? ((birthdayInfo as DateInformation).date ?? "")
				: "",
		address: accountAddressInfo
			? {
					city: (accountAddressInfo as AddressInformation).city,
					country: (accountAddressInfo as AddressInformation).country,
					state: (accountAddressInfo as AddressInformation).state,
					street: (accountAddressInfo as AddressInformation).street,
					zipCode: (accountAddressInfo as AddressInformation).zipCode,
				}
			: null,
		termsOfPaymentEntries: paymentConditions,
	};
}

export function useAccountLoader({ onFetched }: { onFetched: (account: Account) => void }) {
	const { t } = useTranslation();

	const [fetchAccount] = useGetMeLazyQuery({
		fetchPolicy: "no-cache",
	});

	const [fetchPayments] = useGetMyPaymentConditionsLazyQuery({
		fetchPolicy: "no-cache",
	});

	const fetchServerData = useCallback(async (): Promise<GraphqlQueryResult<Account>> => {
		const result = await fetchAccount();
		const paymentResult = await fetchPayments();

		if (result.error) return ResultFromException<Account>(result.errors);
		if (paymentResult.error) return ResultFromException<Account>(paymentResult.errors);

		const node = result.data?.me as unknown as ServerContact;
		if (node?.__typename !== "Contact")
			return ResultFromException<Account>([
				{
					message: "Account not found",
				},
			]);

		const paymentConditions = await mapPaymentConditions(
			paymentResult.data?.myPaymentConditions ?? [],
			t,
		);

		const account = await mapAccount(node, paymentConditions);

		return ResultFromData<Account>(account);
	}, [fetchAccount]);

	const handleComplete = useCallback(
		(account: Account) => {
			onFetched(account);
		},
		[onFetched],
	);

	const options = useMemo(
		() => ({
			onComplete: handleComplete,
			graphqlCall: fetchServerData,
		}),
		[handleComplete, fetchServerData],
	);

	const { save: fetchData, loading } = useNotificationGraphql<void, Account>(options);

	return { fetchData, loading };
}
